/**
 * @file NeedsActionView.tsx
 * @description
 * Enhanced subcomponent for the NEEDS_ACTION status. 
 * Displays IRB feedback from review_data, an "Edit Submission" button,
 * and an explanation of what to do next.
 *
 * Key Features:
 * 1. Shows IRB feedback if submission.review_data is present.
 * 2. Provides an "Edit Submission" button to navigate user to the existing 
 *    SubmissionForm in edit mode (with submissionId).
 * 3. Mentions that once they are done editing, they can resubmit.
 *
 * @dependencies
 * - React
 * - React Router: navigate
 *
 * @notes
 * - Part of Step 4: NEEDS_ACTION features. 
 * - Actual logic to highlight fields requiring changes or re-upload documents 
 *   is done in SubmissionForm & FormSubmissionFields. This view simply 
 *   invites the user to do so and displays any IRB feedback.
 */

import { DocumentDownloadLink } from '../DocumentDownloadLink';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { Document } from '../../lib/supabaseClient';
import FeedbackAndResponses from '../FeedbackAndResponses';
import { SubmissionCapabilities, SubmissionRow } from '../../types/submission';
import { needsActionEditRoute, billingRoute } from '../../utils/submissionRoutes';
import { SubmissionType } from '../../lib/supabaseClient';

interface NeedsActionViewProps {
  capabilities: SubmissionCapabilities;
  submission: SubmissionRow;
}


/**
 * Renders the "Needs Action" UI:
 * - Displays IRB feedback from review_data
 * - Provides an edit button to let user fix or re-upload documents
 */
const NeedsActionView: React.FC<NeedsActionViewProps> = ({ submission, capabilities }) => {
  const navigate = useNavigate();
  const [submissionFormPdfs, setSubmissionFormPdfs] = useState<Document[]>([]);
  
  const supabase = useSupabase();

  const handleEditSubmission = () => {
    // Type-aware: CONTINUING_REVIEW -> /continuing-reviews/:id/respond,
    // NEW_SUBMISSION/AMENDMENT -> /edit-submission?edit=<submissionId>.
    navigate(needsActionEditRoute(submission.id, submission.submission_type as SubmissionType));
  };

  // Fetch submission form PDFs
  useEffect(() => {
    const fetchSubmissionFormPdfs = async () => {
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('submission_id', submission.id)
          .eq('type', 'submission_form_pdf')
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching submission form PDFs:', error);
          return;
        }

        setSubmissionFormPdfs((data || []) as Document[]);
      } catch (error) {
        console.error('Failed to fetch submission form PDFs:', error);
      }
    };

    fetchSubmissionFormPdfs();
  }, [submission.id, supabase]);

  // Generate signed URLs for PDFs


  return (
    <div>
      <h3 className="text-xl font-bold mt-3 mb-3 text-[#F55D3E]">Edits Required Before IRB Review Can Continue</h3>
      <p className="text-gray-700 mb-3">
        The IRB has requested edits for this submission that must be addressed before the review process can proceed.
        {capabilities.canEditSubmission
          ? ' Please review the IRB reviewer feedback below, then click "Edit Submission" to make changes and record your responses to each feedback item.'
          : ' You have read-only shared access, so you can review the feedback and prior PDFs here, but only the submission owner can make changes and respond.'}
      </p>

      {/* Use the new FeedbackAndResponses component */}
      <div className="bg-white rounded-md border border-gray-200 p-4 mb-6">
        {submission.review_data && submission.review_data.review_cycles && submission.review_data.review_cycles.length > 0 ? (
          <FeedbackAndResponses 
            submissionId={submission.id} 
            mode="view"
          />
        ) : (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 text-yellow-800">
            <p className="font-semibold">No IRB feedback details found.</p>
            <p>You may still edit your submission if needed.</p>
          </div>
        )}
      </div>

      {capabilities.canEditSubmission && (
        <div className="flex space-x-4 mb-6">
          <button
            onClick={handleEditSubmission}
            className="inline-flex items-center justify-center bg-[#F55D3E] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#d94a30] focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
          >
            Edit Submission To Address Feedback
          </button>
        </div>
      )}

      {/* Data from Previous Review Cycles section */}
      {submission.review_data && 
       submission.review_data.review_cycles && 
       submission.review_data.review_cycles.length > 1 && (
        <Disclosure as="div" className="mt-4 mb-6">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-blue-900 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
                <span>Feedback & responses from previous review cycles</span>
                <svg
                  className={`w-5 h-5 text-blue-500 ${open ? 'transform rotate-180' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </Disclosure.Button>
              <Disclosure.Panel className="px-4 pt-4 pb-2">
                {submission.review_data?.review_cycles
                  .slice(0, -1) // All cycles except the latest one
                  .map((cycle, index) => (
                    <div key={`previous-cycle-${index}`} className="mb-6 border-b pb-6 last:border-b-0">
                      <h4 className="font-semibold text-gray-700 mb-2">
                        Review Cycle {cycle.cycle} 
                      </h4>
                      <FeedbackAndResponses
                        submissionId={submission.id}
                        cycleNumber={cycle.cycle}
                        mode="view"
                      />
                    </div>
                  ))}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}

      {/* Previous submission form PDFs section */}
      <Disclosure as="div" className="mt-4 mb-6">
        {({ open }) => (
          <>
            <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-blue-900 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
              <span>Previous submission form PDFs</span>
              <svg
                className={`w-5 h-5 text-blue-500 ${open ? 'transform rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-500">
              {submissionFormPdfs.length === 0 ? (
                <p className="text-gray-500 italic">No submission form PDFs found</p>
              ) : (
                <ul className="space-y-2">
                  {submissionFormPdfs.map((doc) => (
                    <li key={doc.id} className="flex items-center">
                      <svg 
                        className="w-5 h-5 text-gray-600 mr-2" 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      <DocumentDownloadLink document={doc} client={supabase} />
                      <span className="text-xs text-gray-500 ml-2">
                        (Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      
      {/* Payment Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">Payment Status</h3>
        {submission.payment_completed ? (
          <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded">
            Payment is complete! 
          </div>
        ) : (
          <div className="p-3 text-[#B83EF5] border border-yellow-200 rounded flex flex-col space-y-3">
            <p>
              Payment has <strong>not</strong> been completed. IRB decisions will be gated until
              payment is received.
            </p>
            {capabilities.canCompletePayment ? (
              <button
                onClick={() => navigate(billingRoute(submission.id))}
                className="px-4 py-2 bg-[#B83EF5] text-white rounded hover:bg-[#9c32c7]-50 self-start"
              >
                Complete Payment
              </button>
            ) : (
              <p className="text-sm text-gray-700">
                Payment is not available for this submission.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NeedsActionView;
